import { useMemo, useCallback, useRef, useEffect } from 'react';
import { tsToMs } from '../utils/chatUtils';

/**
 * useMessageOptimization Hook
 * 
 * Provides message memoization, lazy loading logic, and message filtering.
 * Optimizes message processing for better performance.
 * 
 * @param {Array} firestoreMessages - Messages from Firestore
 * @param {Array} pendingMessages - Pending messages
 * @param {Object} conversation - Current conversation
 * @param {Object} messageProcessing - Message processing utilities
 * @returns {Object} Message optimization utilities
 */
const useMessageOptimization = (firestoreMessages, pendingMessages, conversation, messageProcessing) => {
  const lastMessageCountRef = useRef(0);
  const messageCacheRef = useRef(new Map());

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced memoization for message processing
  const currentMessages = useMemo(() => {
    if (!conversation) return [];
    
    // Merge pending and Firestore messages for instant rendering
    const allMessages = [...(firestoreMessages || []), ...(pendingMessages || [])];
    
    if (conversation.id === 'admin') {
      return allMessages;
    }
    return allMessages;
  }, [conversation, firestoreMessages, pendingMessages]);

  // ⚡️ WHATSAPP-STYLE INSTANT DISPLAY: Optimized message processing (avoid sorting for optimistic messages)
  const sortedAndFilteredMessages = useMemo(() => {
    if (!currentMessages || currentMessages.length === 0) return [];
    
    // ⚡️ SEPARATE OPTIMISTIC: Don't sort optimistic messages (they're already in correct order)
    const optimisticMessages = currentMessages.filter(msg => msg.isOptimistic);
    const nonOptimisticMessages = currentMessages.filter(msg => !msg.isOptimistic);
    
    // ⚡️ DEDUPLICATION: Only for non-optimistic messages
    const uniqueNonOptimistic = nonOptimisticMessages.filter((msg, index, self) => 
      index === self.findIndex(m => {
        // Helper function to safely compare text content
        const getTextContent = (text) => {
          if (typeof text === 'string') return text;
          if (text && typeof text === 'object' && text.sanitized) return text.sanitized;
          return '';
        };
        
        return m.id === msg.id || 
          (getTextContent(m.text) === getTextContent(msg.text) && m.from === msg.from && m.timestamp === msg.timestamp);
      })
    );
    
    // ⚡️ SORTING: Only sort non-optimistic messages (optimistic are already in correct order)
    const sortedNonOptimistic = uniqueNonOptimistic.sort((a, b) => {
      const aTime = tsToMs(a.timestamp ?? a.createdAt ?? a.clientCreatedAt);
      const bTime = tsToMs(b.timestamp ?? b.createdAt ?? b.clientCreatedAt);
      return aTime - bTime; // ascending (old → new)
    });
    
    // ⚡️ INSTANT COMBINE: Existing first, then optimistic (newest at bottom)
    return [...sortedNonOptimistic, ...optimisticMessages];
  }, [currentMessages]);

  // ⚡️ INSTANT DISPLAY: Separate pending messages for immediate rendering
  // ⚡️ WHATSAPP-STYLE INSTANT DISPLAY: Optimized pending messages (no unnecessary processing)
  const pendingMessagesForDisplay = useMemo(() => {
    // ⚡️ INSTANT PROCESSING: Only add status if missing, no other transformations
    return pendingMessages.map(msg => ({
      ...msg,
      status: msg.status || 'sending'
    }));
  }, [pendingMessages]);

  // ⚡️ WHATSAPP-STYLE INSTANT DISPLAY: Show existing messages first, then optimistic messages
  const allVisibleMessages = useMemo(() => {
    // ⚡️ PRIORITY 1: Show existing messages (already sorted and includes pending messages)
    // Note: sortedAndFilteredMessages already includes pending messages from currentMessages merge
    return sortedAndFilteredMessages;
  }, [sortedAndFilteredMessages]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoize message filtering
  const filterMessagesByType = useCallback((messages, type) => {
    const cacheKey = `${type}-${messages.length}`;
    
    if (messageCacheRef.current.has(cacheKey)) {
      return messageCacheRef.current.get(cacheKey);
    }
    
    const filtered = messages.filter(msg => msg.type === type);
    messageCacheRef.current.set(cacheKey, filtered);
    
    // Clean cache if it gets too large
    if (messageCacheRef.current.size > 100) {
      const firstKey = messageCacheRef.current.keys().next().value;
      messageCacheRef.current.delete(firstKey);
    }
    
    return filtered;
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoize message search
  const searchMessages = useCallback((messages, searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) return messages;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return messages.filter(msg => {
      const text = msg.text || '';
      const sanitizedText = typeof text === 'string' ? text : (text.sanitized || '');
      return sanitizedText.toLowerCase().includes(lowerSearchTerm);
    });
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Lazy loading for large message lists
  const getVisibleMessages = useCallback((messages, startIndex = 0, endIndex = 50) => {
    if (messages.length <= endIndex) return messages;
    
    return messages.slice(startIndex, endIndex);
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Message pagination
  const getPaginatedMessages = useCallback((messages, page = 0, pageSize = 50) => {
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    
    return {
      messages: messages.slice(startIndex, endIndex),
      hasMore: endIndex < messages.length,
      totalPages: Math.ceil(messages.length / pageSize),
      currentPage: page
    };
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Message deduplication
  const deduplicateMessages = useCallback((messages) => {
    const seen = new Set();
    return messages.filter(msg => {
      const key = `${msg.id}-${msg.timestamp}-${msg.from}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Message grouping
  const groupMessagesByDate = useCallback((messages) => {
    const groups = {};
    
    messages.forEach(msg => {
      const date = new Date(tsToMs(msg.timestamp || msg.createdAt || msg.clientCreatedAt));
      const dateKey = date.toDateString();
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(msg);
    });
    
    return groups;
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Clear message cache
  const clearMessageCache = useCallback(() => {
    messageCacheRef.current.clear();
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Get cache statistics
  const getCacheStats = useCallback(() => {
    return {
      cacheSize: messageCacheRef.current.size,
      messageCount: allVisibleMessages.length,
      lastMessageCount: lastMessageCountRef.current
    };
  }, [allVisibleMessages.length]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Update message count reference
  useEffect(() => {
    lastMessageCountRef.current = allVisibleMessages.length;
  }, [allVisibleMessages.length]);

  return {
    currentMessages,
    sortedAndFilteredMessages,
    pendingMessagesForDisplay,
    allVisibleMessages,
    filterMessagesByType,
    searchMessages,
    getVisibleMessages,
    getPaginatedMessages,
    deduplicateMessages,
    groupMessagesByDate,
    clearMessageCache,
    getCacheStats
  };
};

export default useMessageOptimization;
